/**
 * HistoryViewer.tsx - HistoryViewer - Visual representation of change history
 * 
 * CHANGE HISTORY:
 * - 251021_0421 [7529d5e] danielbeer: feat: migrate all packages to TypeScript structure
 */

import React, { useState, useEffect } from 'react';
import { useBacktrackr } from './BacktrackrContext';

/**
 * HistoryViewer - Visual representation of change history
 * Shows timeline with branching and allows navigation
 */
const HistoryViewer = ({ onClose }) => {
  const backtrackr = useBacktrackr();
  const [view, setView] = useState('linear'); // 'linear' or 'tree'
  const [history, setHistory] = useState([]);
  const [selectedChange, setSelectedChange] = useState(null);
  const [branches, setBranches] = useState([]);

  useEffect(() => {
    loadHistory();
    loadBranches();
  }, [backtrackr]);

  const loadHistory = () => {
    if (view === 'linear') {
      setHistory(backtrackr.getLinearHistory());
    } else {
      setHistory([backtrackr.getHistoryTree()]);
    }
  };

  const loadBranches = () => {
    setBranches(backtrackr.getBranches());
  };

  const handleGoToChange = async (changeId) => {
    const result = await backtrackr.goToChange(changeId);
    if (result.success) {
      loadHistory();
    }
  };

  const handleCreateBranch = () => {
    const name = prompt('Enter branch name:');
    if (name) {
      backtrackr.createBranch(name, 'User created branch');
      loadBranches();
    }
  };

  const handleSwitchBranch = async (branchId) => {
    await backtrackr.switchBranch(branchId);
    loadHistory();
    loadBranches();
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return date.toLocaleDateString();
  };

  const renderLinearHistory = () => {
    return (
      <div className="history-linear">
        {history.map((node, index) => (
          <div
            key={node.id}
            className={`history-item ${node.id === backtrackr.historyManager.currentChangeId ? 'current' : ''}`}
            onClick={() => setSelectedChange(node)}
            onDoubleClick={() => handleGoToChange(node.id)}
          >
            <div className="history-item-marker">
              <div className="history-dot" />
              {index < history.length - 1 && <div className="history-line" />}
            </div>
            <div className="history-item-content">
              <div className="history-item-header">
                <span className="history-item-type">{node.type}</span>
                <span className="history-item-time">{formatTimestamp(node.timestamp)}</span>
              </div>
              <div className="history-item-description">{node.description}</div>
              {node.context && (
                <div className="history-item-context">
                  <span className="context-badge">{node.context}</span>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderTreeNode = (node, level = 0) => {
    if (!node) return null;

    return (
      <div key={node.id} className="history-tree-node" style={{ marginLeft: `${level * 20}px` }}>
        <div
          className={`history-item ${node.id === backtrackr.historyManager.currentChangeId ? 'current' : ''}`}
          onClick={() => setSelectedChange(node)}
          onDoubleClick={() => handleGoToChange(node.id)}
        >
          <div className="history-item-content">
            <div className="history-item-header">
              <span className="history-item-type">{node.type}</span>
              <span className="history-item-time">{formatTimestamp(node.timestamp)}</span>
            </div>
            <div className="history-item-description">{node.description}</div>
          </div>
        </div>
        {node.children && node.children.length > 0 && (
          <div className="history-tree-children">
            {node.children.map(child => renderTreeNode(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  const renderTreeHistory = () => {
    return (
      <div className="history-tree">
        {history.map(node => renderTreeNode(node))}
      </div>
    );
  };

  return (
    <div className="history-viewer">
      <div className="history-viewer-header">
        <h2>⏱️ History - Backtrackr</h2>
        <button onClick={onClose} className="close-button">×</button>
      </div>

      <div className="history-viewer-toolbar">
        <div className="view-toggle">
          <button
            className={view === 'linear' ? 'active' : ''}
            onClick={() => setView('linear')}
          >
            Linear
          </button>
          <button
            className={view === 'tree' ? 'active' : ''}
            onClick={() => setView('tree')}
          >
            Tree
          </button>
        </div>

        <div className="history-actions">
          <button onClick={handleCreateBranch} title="Create Branch">
            🌿 Branch
          </button>
          <button onClick={loadHistory} title="Refresh">
            🔄
          </button>
        </div>
      </div>

      <div className="history-viewer-content">
        <div className="history-main">
          {view === 'linear' ? renderLinearHistory() : renderTreeHistory()}
        </div>

        {selectedChange && (
          <div className="history-details">
            <h3>Change Details</h3>
            <div className="detail-row">
              <span className="detail-label">Type:</span>
              <span className="detail-value">{selectedChange.type}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Time:</span>
              <span className="detail-value">{new Date(selectedChange.timestamp).toLocaleString()}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Description:</span>
              <span className="detail-value">{selectedChange.description}</span>
            </div>
            {selectedChange.context && (
              <div className="detail-row">
                <span className="detail-label">Context:</span>
                <span className="detail-value">{selectedChange.context}</span>
              </div>
            )}
            {selectedChange.branch && (
              <div className="detail-row">
                <span className="detail-label">Branch:</span>
                <span className="detail-value">{selectedChange.branch}</span>
              </div>
            )}
            
            <div className="detail-actions">
              <button
                onClick={() => handleGoToChange(selectedChange.id)}
                className="goto-button"
              >
                Go to this change
              </button>
            </div>

            {selectedChange.data && (
              <div className="detail-data">
                <h4>Changes</h4>
                <div className="data-diff">
                  {selectedChange.data.delta && (
                    <pre>{JSON.stringify(selectedChange.data.delta.changes, null, 2)}</pre>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="history-viewer-sidebar">
        <h3>Branches</h3>
        <div className="branches-list">
          {branches.map(branch => (
            <div
              key={branch.id}
              className={`branch-item ${branch.id === backtrackr.stats.currentBranch ? 'active' : ''}`}
              onClick={() => handleSwitchBranch(branch.id)}
            >
              <div className="branch-name">{branch.name}</div>
              <div className="branch-info">
                {formatTimestamp(branch.createdAt)}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="history-viewer-footer">
        <div className="stats">
          <span>Total Changes: {backtrackr.stats.totalChanges}</span>
          <span>Contexts: {backtrackr.stats.contexts}</span>
          <span>Branches: {backtrackr.stats.branches}</span>
        </div>
      </div>
    </div>
  );
};

export default HistoryViewer;
